import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { fmtKES as kes } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RefreshCw, TrendingUp, TrendingDown, Users, Wallet, AlertTriangle, DollarSign } from "lucide-react";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
    BarChart, Bar, CartesianGrid, PieChart, Pie, Cell, Legend,
} from "recharts";
import { Jargon } from "@/components/admin/JargonTerm";
import { signTone, toneClassName } from "@/lib/numberColor";

const FMT_KES = new Intl.NumberFormat("en-KE", { maximumFractionDigits: 0 });
const pct = (n) => (n == null ? "\u2014" : `${n > 0 ? "+" : ""}${n}%`);

const COLORS = { individual: "#94a3b8", moe_institution: "#3b82f6", cma_institution: "#006600" };

export default function FinanceDashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [proKes, setProKes] = useState(13000);   // $100 USD @ ~130 KES/USD
    const [cashKes, setCashKes] = useState(0);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/finance/dashboard", {
                params: {
                    // Prize pool retired — hardcoded 0 for backward-compat with the endpoint.
                    prize_kes: 0,
                    emergent_pro_kes: proKes,
                    current_cash_kes: cashKes,
                },
            });
            setData(data);
        } finally {
            setLoading(false);
        }
    }, [proKes, cashKes]);

    useEffect(() => { load(); }, [load]);

    if (!data) {
        return <div className="text-sm text-muted-foreground" data-testid="finance-loading">Loading financial dashboard\u2026</div>;
    }

    const { users, subscription, revenue, churn, profit, growth } = data;
    const profitable = profit.runway_status === "profitable";

    const mixData = [
        { name: "Individual (free)", value: subscription.free || subscription.individual || 0, key: "individual" },
        { name: "MoE Institution",   value: subscription.moe_institution || 0,                 key: "moe_institution" },
        { name: "CMA Institution",   value: subscription.cma_institution || 0,                 key: "cma_institution" },
    ].filter((r) => r.value > 0);

    return (
        <div className="space-y-6" data-testid="finance-dashboard">
            {/* Header + config bar */}
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
                <div>
                    <h3 className="text-2xl font-extrabold tracking-tight flex items-center gap-2">
                        <Wallet className="w-5 h-5 text-primary" /> Financial Dashboard
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                        Live from <span className="mono">users</span>, <span className="mono">payment_transactions</span>, <span className="mono">subscriptions</span>. Refreshed manually to save DB cycles.
                    </p>
                </div>
                <div className="flex flex-wrap items-end gap-2">
                    <div>
                        <label className="text-xs text-muted-foreground block">Emergent Pro (KES/mo)</label>
                        <Input type="number" value={proKes} onChange={(e) => setProKes(+e.target.value || 0)} className="w-32 mono" data-testid="finance-pro-input" />
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground block">Cash on hand (KES)</label>
                        <Input type="number" value={cashKes} onChange={(e) => setCashKes(+e.target.value || 0)} className="w-32 mono" data-testid="finance-cash-input" />
                    </div>
                    <Button size="sm" onClick={load} disabled={loading} data-testid="finance-refresh-btn">
                        <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
                    </Button>
                </div>
            </div>

            {/* KPI row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi
                    testid="finance-mrr"
                    label={<Jargon term="mrr" />}
                    value={kes(revenue.mrr_kes)}
                    sub={<><Jargon term="arr" /> {kes(revenue.arr_kes)}</>}
                    icon={<DollarSign className="w-4 h-4" />}
                    positive
                />
                <Kpi
                    testid="finance-arpu"
                    label={<Jargon term="arpu" />}
                    value={kes(revenue.arpu_kes)}
                    sub={`across ${subscription.total_paying} paying`}
                    icon={<Users className="w-4 h-4" />}
                />
                <Kpi
                    testid="finance-cumulative"
                    label="Cumulative Revenue"
                    value={kes(revenue.cumulative_revenue_kes)}
                    sub={`This month ${kes(revenue.revenue_this_month_kes)}`}
                />
                <Kpi
                    testid="finance-net"
                    label="Net this Month"
                    value={kes(profit.net_this_month_kes)}
                    sub={`Fixed cost ${kes(profit.fixed_monthly_cost_kes)}`}
                    positive={profit.net_this_month_kes >= 0}
                    warn={profit.net_this_month_kes < 0}
                />
            </div>

            {/* Break-even + Runway */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="border border-border bg-card rounded p-4" data-testid="finance-break-even">
                    <div className="overline text-xs text-muted-foreground mb-2"><Jargon term="breakeven" /></div>
                    <div className="flex items-baseline gap-3">
                        <div className="font-extrabold text-3xl">{profit.break_even_paying_users}</div>
                        <div className="text-sm text-muted-foreground">paying users needed</div>
                    </div>
                    <div className="mt-2 text-sm">
                        <span className={`font-semibold ${toneClassName(signTone(profit.break_even_delta, 0))}`}>
                            {profit.break_even_delta >= 0 ? "+" : ""}{profit.break_even_delta} vs current
                        </span>
                        <span className="text-muted-foreground"> ({subscription.total_paying} paying today)</span>
                    </div>
                    {profit.break_even_delta < 0 && (
                        <div className="mt-2 text-xs text-muted-foreground">
                            Need {Math.abs(profit.break_even_delta)} more paying user{Math.abs(profit.break_even_delta) === 1 ? "" : "s"} to cover fixed costs.
                        </div>
                    )}
                </div>

                <div className={`border rounded p-4 ${profitable ? "border-emerald-500/50 bg-emerald-500/5" : "border-amber-500/50 bg-amber-500/5"}`} data-testid="finance-runway">
                    <div className="overline text-xs mb-2 flex items-center gap-2">
                        {profitable ? <TrendingUp className="w-3 h-3 text-emerald-600" /> : <AlertTriangle className="w-3 h-3 text-amber-600" />}
                        <Jargon term="runway" />
                    </div>
                    {profitable ? (
                        <>
                            <div className="font-extrabold text-3xl text-emerald-700">Profitable</div>
                            <div className="text-sm text-emerald-700/80 mt-1">Net-positive this month by {kes(profit.net_this_month_kes)}.</div>
                        </>
                    ) : (
                        <>
                            <div className="font-extrabold text-3xl text-amber-700">
                                {profit.runway_months != null ? `${profit.runway_months} months` : "Set cash above"}
                            </div>
                            <div className="text-sm text-amber-700/80 mt-1">
                                Burning {kes(-profit.net_this_month_kes)}/mo. Enter your cash on hand above to compute runway.
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Charts row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <Chart title="Monthly Revenue (last 12)" testid="finance-chart-revenue">
                    <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={revenue.revenue_by_month}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="month" fontSize={11} />
                            <YAxis fontSize={11} tickFormatter={(v) => FMT_KES.format(v)} />
                            <Tooltip formatter={(v) => kes(v)} />
                            <Bar dataKey="amount" fill="#006600" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </Chart>

                <Chart title="Signups per Month (last 12)" testid="finance-chart-signups">
                    <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={users.signups_by_month}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="month" fontSize={11} />
                            <YAxis fontSize={11} />
                            <Tooltip />
                            <Line type="monotone" dataKey="count" stroke="#922529" strokeWidth={2} dot={{ r: 3 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </Chart>
            </div>

            {/* Tier mix + churn */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <Chart title="Subscription Mix" testid="finance-chart-mix" className="lg:col-span-2">
                    <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                            <Pie data={mixData} dataKey="value" nameKey="name" outerRadius={80} label={(e) => `${e.name}: ${e.value}`}>
                                {mixData.map((r) => <Cell key={r.key} fill={COLORS[r.key] || "#94a3b8"} />)}
                            </Pie>
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </Chart>

                <div className="border border-border bg-card rounded p-4 space-y-3" data-testid="finance-secondary-stats">
                    <div>
                        <div className="overline text-xs text-muted-foreground">Users</div>
                        <div className="text-2xl font-extrabold">{users.total}</div>
                        <div className="text-xs text-muted-foreground">{users.verified} verified \u00b7 {users.active_30d} active (30d)</div>
                    </div>
                    <div>
                        <div className="overline text-xs text-muted-foreground">Paid Conversion</div>
                        <div className="text-2xl font-extrabold" data-testid="finance-conversion">{growth.paid_conversion_rate_pct}%</div>
                        <div className="text-xs text-muted-foreground">{subscription.total_paying} of {users.total}</div>
                    </div>
                    <div>
                        <div className="overline text-xs text-muted-foreground"><Jargon term="churn" /> (30d)</div>
                        <div className="text-2xl font-extrabold">{churn.churn_rate_pct}%</div>
                        <div className="text-xs text-muted-foreground">{churn.downgrades_30d} downgrades \u00b7 {churn.at_risk_stale_billing} at risk</div>
                    </div>
                    <div>
                        <div className="overline text-xs text-muted-foreground"><Jargon term="mom_growth" /></div>
                        <div className="flex items-center gap-4 mt-1">
                            <div>
                                <div className="text-xs text-muted-foreground">Signups</div>
                                <div className={`text-lg font-mono font-bold ${toneClassName(signTone(growth.signup_mom_pct))}`}>{pct(growth.signup_mom_pct)}</div>
                            </div>
                            <div>
                                <div className="text-xs text-muted-foreground">Revenue</div>
                                <div className={`text-lg font-mono font-bold ${toneClassName(signTone(growth.revenue_mom_pct))}`}>{pct(growth.revenue_mom_pct)}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="text-xs text-muted-foreground">
                Last computed {new Date(data.generated_at).toLocaleString()}.
            </div>
        </div>
    );
}

function Kpi({ label, value, sub, icon, positive, warn, testid }) {
    const tone = warn ? "border-red-500/50 bg-red-500/5"
        : positive ? "border-emerald-500/50 bg-emerald-500/5"
            : "border-border bg-card";
    return (
        <div className={`border rounded p-4 ${tone}`} data-testid={testid}>
            <div className="overline text-xs text-muted-foreground mb-1 flex items-center gap-2">
                {icon}{label}
            </div>
            <div className="font-extrabold text-2xl whitespace-nowrap">{value}</div>
            {sub && <div className="text-xs text-muted-foreground mt-1 break-words">{sub}</div>}
        </div>
    );
}

function Chart({ title, testid, className = "", children }) {
    return (
        <div className={`border border-border bg-card rounded p-4 ${className}`} data-testid={testid}>
            <div className="overline text-xs text-muted-foreground mb-3">{title}</div>
            {children}
        </div>
    );
}
